// @flow
import type {
    GetTravelerStayBooking,
    GetStayDisplayStatus,
    Listing,
    TotalPrice,
    TripSummary,
} from './typeDefs';

type Booking = {
    arrival: string,
    checkin: {
        date: string,
        time: string,
    },
    checkout: {
        date: string,
        time: string,
    },
    guests: {
        numberOfChildren: number,
    },
    totalPrice: {
        paymentDetails: TotalPrice,
    },
    reservationStatus: string,
    reservationUuid: string,
};

type Conversation = {
    id: string,
};

type Unit = {
    city: string,
    id: string,
    integratedPropertyManager: boolean,
    listingId: string,
    location: string,
    url: string,
};

type CtaDetails = {
    tripPaymentsLink: String,
};

export type Stay = {
    stay: {
        booking: Booking,
        conversation: Conversation,
        unit: Unit,
    },
};

export const createStayObject = ({
    city,
    conversationId,
    ctaDetails,
    getStayDisplayStatus,
    getTravelerStayBooking,
    gogInvitePopup,
    listing,
    location,
    totals,
    tripSummary,
}: {
    city: string,
    conversationId: string,
    ctaDetails: CtaDetails,
    getStayDisplayStatus: GetStayDisplayStatus,
    getTravelerStayBooking: GetTravelerStayBooking,
    gogInvitePopup: Boolean,
    listing: Listing,
    location: string,
    totals: TotalPrice,
    tripSummary: TripSummary,
}): Stay => {
    const unitUrl: string = listing && listing.detailPageUrl;

    const periodOfStay = getTravelerStayBooking.periodOfStay || {};
    const guests = getTravelerStayBooking.guests || {};
    const isPrimaryGuest = getTravelerStayBooking.isPrimaryGuest;
    const statusCode = getStayDisplayStatus.code || '';
    const statusDescription = getStayDisplayStatus.description || '';
    const {uuid, reservationReferenceNumber} = getTravelerStayBooking;
    const {adults, children}: {adults: number, children: number} = guests;
    const {checkInTime, checkOutTime} = periodOfStay;
    const arrival = periodOfStay.checkInDate;
    const departure = periodOfStay.checkOutDate;
    const checkinDate: string =
        (tripSummary && tripSummary.arrivalDate) || arrival;
    const checkoutDate: string =
        (tripSummary && tripSummary.departureDate) || departure;
    const {listingNumber: listingId, contact} = listing || {};
    const name = contact?.name || '';
    const {
        integratedPropertyManager,
        ipmGuaranteedPricingActive,
        ownerManaged,
        unitApiUrl,
    } = listing;

    return {
        stay: {
            booking: {
                arrival,
                checkin: {
                    date: checkinDate,
                    time: checkInTime,
                },
                checkout: {
                    date: checkoutDate,
                    time: checkOutTime,
                },
                departure,
                guests: {
                    numberOfAdults: adults,
                    numberOfChildren: children,
                },
                isPrimaryGuest,
                totalPrice: {
                    paymentDetails: totals,
                },
                status: {
                    code: statusCode,
                    description: statusDescription,
                },
                reservationStatus: tripSummary && tripSummary.reservationStatus,
                reservationUuid: uuid,
            },
            ctaDetails,
            conversation: {
                id: conversationId,
            },
            gogInvitePopup,
            ownerManaged,
            unit: {
                city,
                hostName: name,
                id: reservationReferenceNumber,
                integratedPropertyManager,
                ipmGuaranteedPricingActive,
                listingId,
                location,
                ownerManaged,
                unitApiUrl,
                url: unitUrl,
            },
        },
    };
};

export const getReservationUuidFromStayContextData = (stayData: Stay) => {
    return stayData.stay.booking.reservationUuid;
};
